import * as macrosApi from '@/api/macros'
import { normalizeMacros, sleep, isQlcMacro } from '@/widgets/macros'

function mapApiMacro(m) {
  if (!m) return null
  const steps = Array.isArray(m.steps)
    ? m.steps
    : (typeof m.steps === 'string' ? JSON.parse(m.steps || '[]') : m.steps || [])
  const [normalized] = normalizeMacros([{
    label: m.name,
    color: m.color,
    icon: m.icon,
    stopOnError: m.stop_on_error !== false,
    steps,
  }])
  return {
    id: m.id,
    name: m.name,
    color: normalized?.color || m.color || '#4798e8',
    icon: normalized?.icon || m.icon || 'play',
    stopOnError: m.stop_on_error !== false,
    steps: normalized?.steps || [],
    created_at: m.created_at,
    updated_at: m.updated_at,
  }
}

function toApiPayload(form) {
  const [normalized] = normalizeMacros([{
    label: form.name || form.label,
    color: form.color,
    icon: form.icon,
    stopOnError: form.stopOnError !== false && form.stop_on_error !== false,
    steps: form.steps,
  }])
  return {
    name: String(form.name || form.label || '').trim(),
    color: normalized?.color || '#4798e8',
    icon: normalized?.icon || 'play',
    stop_on_error: form.stopOnError !== false && form.stop_on_error !== false,
    steps: normalized?.steps || [],
  }
}

const state = {
  macros: [],
  loaded: false,
}

const getters = {
  macros: (s) => s.macros,
  loaded: (s) => s.loaded,
  byId: (s) => (id) => s.macros.find((m) => m.id === Number(id)),
  qlcMacros: (s) => s.macros.filter(isQlcMacro),
  generalMacros: (s) => s.macros.filter((m) => !isQlcMacro(m)),
}

const mutations = {
  SET_MACROS(s, list) {
    s.macros = list
    s.loaded = true
  },
  UPSERT_MACRO(s, macro) {
    const idx = s.macros.findIndex((m) => m.id === macro.id)
    if (idx === -1) s.macros.push(macro)
    else s.macros.splice(idx, 1, macro)
    s.macros.sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id)
  },
  REMOVE_MACRO(s, id) {
    s.macros = s.macros.filter((m) => m.id !== id)
  },
}

const actions = {
  async fetchMacros({ commit }) {
    const { data } = await macrosApi.listMacros()
    const list = (data.data.macros || []).map(mapApiMacro).filter(Boolean)
    commit('SET_MACROS', list)
    return list
  },

  async createMacro({ commit }, form) {
    const { data } = await macrosApi.createMacro(toApiPayload(form))
    const macro = mapApiMacro(data.data.macro)
    commit('UPSERT_MACRO', macro)
    return macro
  },

  async updateMacro({ commit }, { id, ...form }) {
    const { data } = await macrosApi.updateMacro(id, toApiPayload(form))
    const macro = mapApiMacro(data.data.macro)
    commit('UPSERT_MACRO', macro)
    return macro
  },

  async deleteMacro({ commit }, id) {
    await macrosApi.deleteMacro(id)
    commit('REMOVE_MACRO', id)
  },

  async runMacro({ dispatch }, payload) {
    const macro = payload && payload.macro ? payload.macro : payload
    const stack = (payload && payload.stack) || []
    const steps = (macro && macro.steps) || []
    if (!steps.length) {
      throw new Error('Macro has no steps')
    }
    const id = Number(macro.id)
    let nextStack = stack
    if (Number.isFinite(id) && id > 0) {
      if (stack.includes(id)) {
        throw new Error(`Circular macro reference (#${id})`)
      }
      nextStack = [...stack, id]
    }
    const stopOnError = macro.stopOnError !== false
    let done = 0
    let failed = 0
    let lastErr = ''
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]
      try {
        await dispatch('execStep', { step, stack: nextStack })
        done++
      } catch (e) {
        failed++
        lastErr = e.response?.data?.message || e.message || 'Failed'
        if (stopOnError) {
          const err = new Error(`Stopped at step ${i + 1}: ${lastErr}`)
          err.done = done
          err.failed = failed
          err.stepIndex = i
          throw err
        }
      }
    }
    return { done, failed, lastErr, total: steps.length }
  },

  async execStep({ dispatch, getters }, { step, stack = [] }) {
    if (step.kind === 'delay') {
      await sleep(step.delayMs || 0)
      return
    }
    if (step.kind === 'alert') {
      const body = {
        message: step.message,
        color: step.color || '#ffbc34',
        icon: step.icon || 'bell',
      }
      if (step.nodeId && step.nodeId !== 'all') {
        body.node_id = step.nodeId
      }
      await dispatch('mission/sendAlert', body, { root: true })
      return
    }
    if (step.kind === 'macro') {
      const nested = getters.byId(step.macroId)
      if (!nested) {
        throw new Error(`QLC macro #${step.macroId} not found`)
      }
      await dispatch('runMacro', { macro: nested, stack })
      return
    }
    await dispatch('mission/sendCommand', {
      node_id: step.nodeId,
      adapter: step.adapter,
      action: step.action,
      payload: step.payload || {},
    }, { root: true })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
